import { useEffect, useState, useCallback } from 'react'
import type { FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Trash2 } from 'lucide-react'
import SEO from '@shared/components/SEO'
import { apiGet, apiPost, apiDelete, ApiError } from '@shared/libs/apiClient'
import { useAuth } from '@shared/stores/AuthContext'
import type { ForumCategory, ForumPost } from './DiscussionForum'

interface ForumComment {
    id: string
    postId: string
    content: string
    authorId: string
    authorName: string
    createdAt: string
}

interface PostWithComments extends ForumPost {
    comments?: ForumComment[]
}

function fmtDate(iso: string): string {
    try {
        return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
    } catch {
        return ''
    }
}

const CATEGORY_LABEL: Record<string, string> = { perspective: 'Perspective', commentary: 'Commentary' }

export default function ForumPostDetail() {
    const { category = 'perspective', id = '' } = useParams<{ category: ForumCategory; id: string }>()
    const { user } = useAuth()

    const [post, setPost] = useState<PostWithComments | null>(null)
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState<string | null>(null)

    const [comment, setComment] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [commentErr, setCommentErr] = useState<string | null>(null)

    const load = useCallback(() => {
        setLoading(true)
        setLoadError(null)
        apiGet<{ success: boolean; post: PostWithComments }>(`/forum/${id}`)
            .then((r) => setPost(r.post))
            .catch((e) => setLoadError(e instanceof ApiError ? e.message : 'Failed to load this discussion.'))
            .finally(() => setLoading(false))
    }, [id])

    useEffect(() => {
        load()
    }, [load])

    const canModerate = (authorId: string) =>
        !!user && (user.role === 'admin' || user.role === 'editor' || user.id === authorId)

    const addComment = async (e: FormEvent) => {
        e.preventDefault()
        if (!comment.trim()) return
        setSubmitting(true)
        setCommentErr(null)
        try {
            const r = await apiPost<{ success: boolean; comment: ForumComment }>(`/forum/${id}/comments`, {
                content: comment.trim()
            })
            setPost((prev) => (prev ? { ...prev, comments: [...(prev.comments ?? []), r.comment] } : prev))
            setComment('')
        } catch (err) {
            setCommentErr(err instanceof ApiError ? err.message : 'Could not post your reply.')
        } finally {
            setSubmitting(false)
        }
    }

    const deleteComment = async (commentId: string) => {
        if (!window.confirm('Delete this reply?')) return
        try {
            await apiDelete(`/forum/${id}/comments/${commentId}`)
            setPost((prev) => (prev ? { ...prev, comments: (prev.comments ?? []).filter((c) => c.id !== commentId) } : prev))
        } catch (err) {
            alert(err instanceof ApiError ? err.message : 'Failed to delete.')
        }
    }

    const backTo = `/discussion/${category}`

    if (loading) {
        return (
            <section className="section">
                <div className="container-prose text-center" style={{ color: 'var(--color-ink-400)' }}>Loading…</div>
            </section>
        )
    }

    if (loadError || !post) {
        return (
            <section className="section">
                <SEO title="Discussion not found" path={backTo} noIndex />
                <div className="container-prose text-center">
                    <div className="eyebrow eyebrow--center">Not found</div>
                    <h1 className="font-display mt-3" style={{ fontSize: '2rem', color: 'var(--color-ink-700)' }}>
                        This discussion is unavailable
                    </h1>
                    <Link to={backTo} className="btn btn-ghost mt-6 inline-block">← Back</Link>
                </div>
            </section>
        )
    }

    const comments = post.comments ?? []

    return (
        <>
            <SEO title={post.title} description={post.content.slice(0, 160)} path={`/discussion/${category}/${id}`} type="article" />

            <section className="section">
                <div className="container-prose max-w-3xl mx-auto">
                    <Link
                        to={backTo}
                        className="inline-block text-[11px] uppercase font-semibold"
                        style={{ letterSpacing: '0.18em', color: 'var(--color-gold-700)' }}
                    >
                        ← {CATEGORY_LABEL[category] ?? 'Discussion'}
                    </Link>

                    <article className="mt-5">
                        <div className="flex items-start justify-between gap-4">
                            <h1
                                className="font-display"
                                style={{ fontSize: 'clamp(1.6rem, 3vw, 2.4rem)', color: 'var(--color-ink-700)', letterSpacing: '-0.02em', lineHeight: 1.1 }}
                            >
                                {post.title}
                            </h1>
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-x-3 text-[12.5px]" style={{ color: 'var(--color-ink-400)' }}>
                            <span className="font-semibold" style={{ color: 'var(--color-ink-600)' }}>{post.authorName}</span>
                            <span>·</span>
                            <span>{fmtDate(post.createdAt)}</span>
                            <span>·</span>
                            <span className="uppercase" style={{ letterSpacing: '0.12em', color: 'var(--color-gold-700)' }}>
                                {CATEGORY_LABEL[category] ?? category}
                            </span>
                        </div>

                        <div
                            className="mt-7 text-[16px] leading-relaxed"
                            style={{ color: 'var(--color-ink-600)', whiteSpace: 'pre-wrap' }}
                        >
                            {post.content}
                        </div>
                    </article>

                    <div className="mt-14">
                        <div className="eyebrow">{comments.length} {comments.length === 1 ? 'Reply' : 'Replies'}</div>

                        <ul className="mt-6" style={{ borderTop: '1px solid var(--color-rule)' }}>
                            {comments.map((c) => (
                                <li key={c.id} className="py-5" style={{ borderBottom: '1px solid var(--color-rule)' }}>
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex flex-wrap items-center gap-x-3 text-[12.5px]" style={{ color: 'var(--color-ink-400)' }}>
                                            <span className="font-semibold" style={{ color: 'var(--color-ink-600)' }}>{c.authorName}</span>
                                            <span>·</span>
                                            <span>{fmtDate(c.createdAt)}</span>
                                        </div>
                                        {canModerate(c.authorId) && (
                                            <button
                                                onClick={() => deleteComment(c.id)}
                                                aria-label="Delete reply"
                                                className="shrink-0 p-1"
                                                style={{ color: 'var(--color-ink-300)' }}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                    <p className="mt-2 text-[15px] leading-relaxed" style={{ color: 'var(--color-ink-600)', whiteSpace: 'pre-wrap' }}>
                                        {c.content}
                                    </p>
                                </li>
                            ))}
                        </ul>

                        {comments.length === 0 && (
                            <p className="mt-4 text-[14px]" style={{ color: 'var(--color-ink-400)' }}>
                                No replies yet.
                            </p>
                        )}

                        {user ? (
                            <form onSubmit={addComment} className="mt-8">
                                <textarea
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    placeholder="Write a reply…"
                                    rows={4}
                                    className="input"
                                />
                                {commentErr && (
                                    <div className="mt-2 text-[13.5px]" style={{ color: '#8B1A2D' }}>{commentErr}</div>
                                )}
                                <div className="mt-3 flex items-center gap-3">
                                    <button type="submit" disabled={submitting} className="btn btn-accent">
                                        {submitting ? 'Posting…' : 'Post reply'}
                                    </button>
                                    <span className="text-[12.5px]" style={{ color: 'var(--color-ink-400)' }}>
                                        Replying as {user.name}
                                    </span>
                                </div>
                            </form>
                        ) : (
                            <div className="mt-8 p-5 text-center" style={{ background: 'var(--color-paper-100)', border: '1px solid var(--color-rule)' }}>
                                <Link
                                    to={`/dashboard/login?redirect=${encodeURIComponent(`/discussion/${category}/${id}`)}`}
                                    className="btn btn-gold inline-flex"
                                >
                                    Sign in to reply
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </>
    )
}
